import type { RealMachinesDatabase } from '@/db/database'
import { APP_VERSION } from '@/app/version'
import { getDatabase } from '@/db/database'
import { createDefaultSettings } from '@/db/defaults'
import { executeDatabaseOperation } from '@/db/errors'
import { settingsSchema } from '@/db/schemas'
import { inspectStorage, type StorageStatus } from '@/db/services/storageService'
import { DATABASE_SCHEMA_VERSION } from '@/db/version'

export interface DataLayerStatus {
  schemaVersion: number
  storage: StorageStatus
}

let singletonInitialization: Promise<DataLayerStatus> | undefined

async function initialize(database: RealMachinesDatabase): Promise<DataLayerStatus> {
  await executeDatabaseOperation('inicializar banco local', async () => {
    await database.open()
    await database.transaction('rw', database.settings, async () => {
      const settings = await database.settings.get('app')
      if (!settings) {
        await database.settings.add(settingsSchema.parse(createDefaultSettings()))
      } else {
        const synchronizedSettings = settingsSchema.parse({
          ...settings,
          appVersion: APP_VERSION,
          schemaVersion: DATABASE_SCHEMA_VERSION,
          updatedAt:
            settings.appVersion === APP_VERSION &&
            settings.schemaVersion === DATABASE_SCHEMA_VERSION
              ? settings.updatedAt
              : new Date().toISOString(),
        })
        await database.settings.put(synchronizedSettings)
      }
    })
  })

  let storage: StorageStatus
  try {
    storage = await inspectStorage(undefined, false)
  } catch {
    storage = {
      supported: false,
      persisted: false,
      persistenceRequested: false,
      usage: 0,
      quota: 0,
      percentage: 0,
      warning: false,
      critical: false,
      level: 'unknown',
    }
  }
  return { schemaVersion: DATABASE_SCHEMA_VERSION, storage }
}

export function initializeDataLayer(
  database: RealMachinesDatabase = getDatabase(),
): Promise<DataLayerStatus> {
  if (database !== getDatabase()) return initialize(database)
  singletonInitialization ??= initialize(database)
  return singletonInitialization
}
